import React, { useState, useEffect } from 'react';
import { ProcessButtons } from './ProcessButtons';
import { EventHeader } from './EventHeader';
import { ParticipantsTable } from './ParticipantsTable';

export const EventDetailsView = ({
  selectedEvent,
  handleBackToList,
  filterStatus,
  setFilterStatus,
  currentPage,
  setCurrentPage,
  itemsPerPage,
  setItemsPerPage,
  sortField,
  sortDirection,
  handleSort,
  handleParticipantClick,
  formatCurrency,
  formatDate,
  onSettlementProcess,
  onReprocess,
  isProcessing,
  searchParticipant,
  setSearchParticipant
}) => {
  const [filteredParticipants, setFilteredParticipants] = useState([]);

  useEffect(() => {
    if (!selectedEvent) return;

    const filtered = selectedEvent.participants.filter((participant) => {
      const matchesStatus = filterStatus === 'all' || participant.status === filterStatus;
      const search = searchParticipant.toLowerCase();
      const matchesSearch =
        participant.name?.toLowerCase().includes(search) ||
        participant.email?.toLowerCase().includes(search) ||
        participant.status?.toLowerCase().includes(search) ||
        participant.amount?.toString().includes(search) ||
        formatDate(participant.joinDate)?.toLowerCase().includes(search);

      return matchesStatus && matchesSearch;
    });

    // Sort participants
    const sorted = [...filtered].sort((a, b) => {
      if (!sortField) return 0;

      let aValue = a[sortField];
      let bValue = b[sortField];

      if (sortField === 'joinDate') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredParticipants(sorted);
  }, [selectedEvent, filterStatus, searchParticipant, sortField, sortDirection, formatDate]);

  if (!selectedEvent) return null;

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentParticipants = filteredParticipants.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Back Button and Actions */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={handleBackToList}
          className="group inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-all duration-300 hover:gap-3 font-semibold"
        >
          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </div>
          Back to Overview
        </button>
        <ProcessButtons
          onSettlement={onSettlementProcess}
          onReprocess={onReprocess}
          isProcessing={isProcessing}
        />
      </div>

      {/* Event Header */}
      <EventHeader event={selectedEvent} />

      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          {
            label: 'Total Capacity',
            value: selectedEvent.totalParticipants || 'Unlimited',
            color: 'blue',
            icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z'
          },
          {
            label: 'Paid',
            value: selectedEvent.successCount,
            color: 'green',
            icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
          },
          {
            label: 'Not Paid',
            value: selectedEvent.cancelledCount,
            color: 'orange',
            icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
          },
          {
            label: 'Registered',
            value: selectedEvent.successCount + selectedEvent.cancelledCount,
            color: 'purple',
            icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'
          }
        ].map((stat, index) => (
          <div
            key={index}
            className={`bg-gradient-to-br from-white to-${stat.color}-50 rounded-xl p-4 shadow-md border border-${stat.color}-100`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg bg-${stat.color}-100 flex items-center justify-center`}>
                <svg className={`w-5 h-5 text-${stat.color}-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={stat.icon} />
                </svg>
              </div>
              <div>
                <div className={`text-xl font-bold text-${stat.color}-600`}>{stat.value}</div>
                <div className="text-xs text-gray-600">{stat.label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Financial Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-gradient-to-br from-white to-green-50 rounded-2xl p-6 shadow-lg border border-green-100">
          <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
              ₹
            </div>
            Amount Summary
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center p-4 bg-white rounded-xl border border-green-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Paid Amount</span>
                  <div className="text-sm text-green-600">{selectedEvent.successCount} participant(s)</div>
                </div>
              </div>
              <span className="text-xl font-bold text-green-600">
                {formatCurrency(selectedEvent.successAmount)}
              </span>
            </div>
            <div className="flex justify-between items-center p-4 bg-white rounded-xl border border-orange-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Not Paid</span>
                  <div className="text-sm text-orange-600">{selectedEvent.cancelledCount} participant(s)</div>
                </div>
              </div>
              <span className="text-xl font-bold text-orange-600">
                {formatCurrency(selectedEvent.cancelledAmount)}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-white to-blue-50 rounded-2xl p-6 shadow-lg border border-blue-100">
          <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            Event Details
          </h3>
          <div className="space-y-3">
            {[
              { label: 'Event Type', value: `${selectedEvent.category}`, icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10' },
              { label: 'Date', value: new Date(selectedEvent.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }), icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
              { label: 'Venue', value: selectedEvent.venue, icon: 'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z' }
            ].map((detail, index) => (
              <div key={index} className="flex justify-between items-center p-3 bg-white rounded-lg border border-gray-200">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-md bg-blue-100 flex items-center justify-center">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={detail.icon} />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-gray-600">{detail.label}:</span>
                </div>
                <span className="text-sm font-semibold text-gray-800">{detail.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Participants Table */}
      <ParticipantsTable
        participants={currentParticipants}
        totalParticipants={filteredParticipants.length}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
        onItemsPerPageChange={setItemsPerPage}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
        onParticipantClick={handleParticipantClick}
        filterStatus={filterStatus}
        setFilterStatus={setFilterStatus}
        searchParticipant={searchParticipant}
        setSearchParticipant={setSearchParticipant}
        formatCurrency={formatCurrency}
        formatDate={formatDate}
      />
    </div>
  );
};